// Global fetch interceptor that forwards the platform-admin "impersonate"
// tenant slug as `X-Tenant-Slug` on every same-origin `/api/*` request.
//
// Why this exists
// ----------------
// When a platform super-admin clicks "انتحال" (impersonate) on a tenant,
// the server sets `req.session.adminId` + `req.session.impersonatedTenantSlug`
// and returns `redirectUrl: /admin?_tenant=<slug>`. The frontend opens a
// new tab at `nexuscart.co/admin?_tenant=<slug>`.
//
// The tenant resolver on the API side already supports two paths to pick
// up that tenant: (a) the `?_tenant=<slug>` query, (b) the
// `sess.impersonatedTenantSlug` fallback. In production we have seen the
// new tab still bounce to `/admin/login` — most likely because the very
// first `/api/auth/admin/me` request is fired BEFORE the React router
// pushes the query into wouter / before any per-page code remembers to
// add `_tenant` to its fetches, so neither (a) nor (b) reliably wins.
//
// This interceptor is the belt-and-braces fix: capture the slug ONCE
// from the page URL, persist it in sessionStorage for the tab's
// lifetime, and stamp it as `X-Tenant-Slug` on every `/api/*` request
// the page makes. The header is only added when the slug is present, so
// non-impersonating tabs are unaffected.

const STORAGE_KEY = "impersonateTenantSlug";

function readSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("_tenant");
    if (fromUrl) {
      try { sessionStorage.setItem(STORAGE_KEY, fromUrl); } catch {}
      return fromUrl;
    }
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getImpersonateTenantSlug(): string | null {
  return readSlug();
}

// Clear the persisted impersonation slug. MUST be called when the
// platform admin ends impersonation, otherwise the fetch interceptor
// keeps stamping `X-Tenant-Slug` on every `/api/*` request for the rest
// of the tab's life — including `/api/_meta/host`, which then resolves
// the platform host (nexuscart.co) as a TENANT. That makes CurrentLayout
// wrap the platform-admin panel in the storefront chrome, so the tenant
// storefront header stays "stuck" on top of /platform-admin pages.
export function clearImpersonateTenantSlug(): void {
  if (typeof window === "undefined") return;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

export function installImpersonateTenantHeader() {
  if (typeof window === "undefined") return;
  const w = window as any;
  if (w.__impersonateFetchInstalled) return;
  w.__impersonateFetchInstalled = true;

  // Prime sessionStorage from the URL so the slug survives in-tab
  // navigations (e.g. AdminLayout redirecting to /admin/login).
  readSlug();

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const slug = readSlug();
    if (!slug) return originalFetch(input, init);

    // Determine the URL string for cheap inspection. Skip non /api paths,
    // cross-origin URLs, and anything that already pinned a tenant slug.
    let urlStr: string;
    if (typeof input === "string") urlStr = input;
    else if (input instanceof URL) urlStr = input.toString();
    else urlStr = (input as Request).url;

    let isApi = false;
    try {
      const u = new URL(urlStr, window.location.origin);
      if (u.origin !== window.location.origin) return originalFetch(input, init);
      isApi = u.pathname.includes("/api/");
    } catch {
      isApi = urlStr.includes("/api/");
    }
    if (!isApi) return originalFetch(input, init);

    // Merge the X-Tenant-Slug header without clobbering an existing one.
    const merged: HeadersInit = (() => {
      const existing = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      if (!existing.has("X-Tenant-Slug")) existing.set("X-Tenant-Slug", slug);
      return existing;
    })();

    const nextInit: RequestInit = { ...(init || {}), headers: merged };
    // Always send credentials so the session cookie travels with the
    // tagged request — same-origin fetch defaults to "same-origin" which
    // is fine, but be explicit for any cross-helper consistency.
    if (!nextInit.credentials) nextInit.credentials = "include";
    return originalFetch(input, nextInit);
  };
}
